/**
 * AdvisoryBoardEmpty — J5 GAP 8
 *
 * Shown when a tenant has zero knowledge_persona actors assigned to a domain.
 * Factored out so J7 Council view can reuse the same empty state.
 *
 * PRD §12.3.8 task 5 — "An AdvisoryBoardEmpty component is factored out for
 * reuse in J7's Council view."
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { t } from '@/lib/i18n';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface AdvisoryBoardEmptyProps {
  /** The domain ID — used to pre-fill the create-persona deep link. */
  domainId?: string | null;
  /** Additional CSS classes */
  className?: string;
}

export function AdvisoryBoardEmpty({ domainId, className }: AdvisoryBoardEmptyProps) {
  // Deep link: /roros/actors?type=knowledge_persona&create=true
  // Optionally pre-select domain if known.
  const params = new URLSearchParams({ type: 'knowledge_persona', create: 'true' });
  if (domainId) params.set('domainId', domainId); // W3-A1: camelCase URL query param
  const createLink = `/roros/actors?${params.toString()}`;

  return (
    <div
      className={`flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 py-6 px-4 text-center ${className ?? ''}`}
      role="status"
      aria-label={t('No Advisory Board configured')}
    >
      <Users className="h-8 w-8 text-muted-foreground" aria-hidden />
      <div>
        <p className="text-sm font-medium text-foreground">
          {t('No Advisory Board configured for this domain')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('Create a Knowledge Persona in the Actor Registry to enable consultation.')}
        </p>
      </div>
      {/* Use buttonVariants so the link inherits the design system's primary button styles
          (theme tokens, dark-mode, focus rings) instead of duplicating raw CSS. */}
      <Link
        to={createLink}
        className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'mt-1 gap-1.5 text-xs')}
        data-testid="advisory-board-empty-create-link"
      >
        {t('Create Knowledge Persona')}
      </Link>
    </div>
  );
}
